import { useContext } from 'react';
import { DrawerStackContextType, DrawerItem } from './types';
import { DrawerStackContext } from './context';

// Hook to use the drawer stack context (can return null)
export function useDrawerStack(): DrawerStackContextType | null {
  return useContext(DrawerStackContext);
}

// Hook to use the drawer stack context (throws if not found)
export function useDrawerStackRequired(): DrawerStackContextType {
  const context = useContext(DrawerStackContext);
  if (!context) {
    throw new Error('useDrawerStackRequired must be used within a DrawerStackProvider');
  }
  return context;
}

// Hook to get the level of a specific drawer
export function useDrawerLevel(id: string): number {
  const drawerStack = useDrawerStack();
  return drawerStack?.getDrawerLevel(id) ?? -1;
}

// Hook to get the z-index for a specific drawer
export function useDrawerZIndex(id: string): number {
  const level = useDrawerLevel(id);
  const baseZIndex = 10000;
  return level >= 0 ? baseZIndex + (level * 100) : baseZIndex;
}

// Hook to check if a drawer type can nest
export function useCanNest(type: string): boolean {
  const drawerStack = useDrawerStack();
  return drawerStack?.canNest(type) ?? true;
}

// Hook to check if a drawer should replace the current one
export function useShouldReplace(incoming: Omit<DrawerItem, 'level' | 'timestamp'>): boolean {
  const drawerStack = useDrawerStack();
  return drawerStack?.shouldReplace(incoming) ?? false;
}
